import React from 'react'
import {
    Breadcrumbs as MUIBreadcrumbs,
    Link,
    Typography,
    styled,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Icon } from 'packages/eid-icons'
import { Loader, Tooltip } from 'packages/eid-ui'

const StyledLink = styled(Link)(({ theme, disabled }) => ({
    color: disabled ? '#7d7c7c' : '#307fc1',
    textDecoration: 'none',
    cursor: disabled ? 'default' : 'pointer',
    fontSize: '14px',
    '&:hover': {
        textDecoration: disabled ? 'none' : 'underline',
    },
}))

const StyledBreadcrumbs = styled(MUIBreadcrumbs)({
    '& .MuiBreadcrumbs-separator': {
        color: '#8b909a',
        margin: '0 8px',
        display: 'flex',
        alignItems: 'center',
    },
})

const LoaderWrapper = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: '50px', // Prevent layout shifts
})

const truncateText = (text, maxLength = 20, disableTruncation = false) => {
    if (disableTruncation || !text || text.length <= maxLength) {
        return text
    }
    return `${text.substring(0, maxLength)}...`
}

/**
 * Generic Breadcrumbs component that accepts an array of breadcrumb items
 * @param {Object[]} breadcrumbs - Array of breadcrumb items
 * @param {string} [breadcrumbs[].labelKey] - Translation key for the breadcrumb label
 * @param {string} [breadcrumbs[].label] - Direct label text (takes precedence over labelKey)
 * @param {string} [breadcrumbs[].url] - URL to navigate to when clicked
 * @param {Function} [breadcrumbs[].onClick] - Click handler function (takes precedence over url)
 * @param {boolean} [breadcrumbs[].loading] - Whether this breadcrumb item is in loading state
 * @param {boolean} [disableTruncation] - Whether to disable text truncation
 */
const Breadcrumbs = ({ breadcrumbs = [], disableTruncation = false }) => {
    const { t } = useTranslation()
    const history = useHistory()

    const handleClick = (item, event) => {
        event.preventDefault()
        if (item.onClick) {
            item.onClick()
        } else if (item.url) {
            history.push(item.url)
        }
    }

    const renderBreadcrumbContent = (item) => {
        const fullLabel = item.label || t(item.labelKey)
        const truncatedLabel = truncateText(fullLabel, 20, disableTruncation)

        if (fullLabel !== truncatedLabel) {
            return (
                <Tooltip title={fullLabel}>
                    <span>{truncatedLabel}</span>
                </Tooltip>
            )
        }

        return truncatedLabel
    }

    return (
        <StyledBreadcrumbs
            aria-label="breadcrumb"
            separator={
                <Icon
                    name="Arrow"
                    width="1.6rem"
                    height="1.6rem"
                    color="#8b909a"
                    direction="right"
                />
            }
        >
            {breadcrumbs.map((item, index) => {
                const isLast = index === breadcrumbs.length - 1

                if (item.loading) {
                    return (
                        <LoaderWrapper key={index}>
                            <Loader height={20} />
                        </LoaderWrapper>
                    )
                }

                return (
                    <StyledLink
                        key={item.labelKey || item.label || index}
                        component="button"
                        variant="body1"
                        onClick={(e) => !isLast && handleClick(item, e)}
                        disabled={isLast || item.disabled}
                        underline="none"
                    >
                        {renderBreadcrumbContent(item)}
                    </StyledLink>
                )
            })}
        </StyledBreadcrumbs>
    )
}

export default Breadcrumbs
